import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useMemo, useState} from 'react'
import {NegativeFeedbackButton, PositiveFeedbackButton} from './Buttons'
import {useHandleFeedbackSubmit} from './hooks'
import type {FeedbackOptions} from './types'

export interface CopilotCodeReviewFeedbackProps {
  commentId: string
  commentUrl?: string
  feedbackPath?: string
  feedbackOptions?: FeedbackOptions
  additionalParameters?: Record<string, string>
  showNegativeFeedbackModal?: boolean
}

const Feedback: React.FC<CopilotCodeReviewFeedbackProps> = ({
  commentId,
  commentUrl,
  feedbackPath,
  feedbackOptions,
  additionalParameters,
  showNegativeFeedbackModal = true,
}) => {
  const [typeSubmitted, setTypeSubmitted] = useState<'POSITIVE' | 'NEGATIVE'>()

  const path = useMemo(() => {
    if (feedbackPath) return feedbackPath
    if (commentUrl) {
      const parsedPath = new URL(commentUrl, ssrSafeLocation.origin)
      parsedPath.hash = ''
      return `${parsedPath}/code_review_feedback`
    }

    throw new Error('missing feedbackPath and commentUrl')
  }, [commentUrl, feedbackPath])

  const handleSubmit = useHandleFeedbackSubmit({path, additionalParameters})

  const handleClick = (props: {
    commentId: string
    type: 'POSITIVE' | 'NEGATIVE'
    feedbackChoice?: string[]
    textResponse?: string
  }) => {
    setTypeSubmitted(props.type)
    handleSubmit(props)
  }

  return (
    <div data-testid={`copilot-code-review-feedback-${commentId}`} className="d-flex gap-1">
      {typeSubmitted !== 'NEGATIVE' && (
        <PositiveFeedbackButton disabled={!!typeSubmitted} commentId={commentId} onClick={handleClick} />
      )}
      {typeSubmitted !== 'POSITIVE' && (
        <NegativeFeedbackButton
          disabled={!!typeSubmitted}
          onClick={handleClick}
          commentId={commentId}
          feedbackOptions={feedbackOptions}
          showNegativeFeedbackModal={showNegativeFeedbackModal}
        />
      )}
    </div>
  )
}

export const CopilotCodeReviewFeedback: React.FC<CopilotCodeReviewFeedbackProps> = props => (
  <ErrorBoundary boundaryName="CopilotCodeReviewFeedback" fallback={null}>
    <Feedback {...props} />
  </ErrorBoundary>
)

try{ Feedback.displayName ||= 'Feedback' } catch {}
try{ CopilotCodeReviewFeedback.displayName ||= 'CopilotCodeReviewFeedback' } catch {}